import { z } from "zod";
import type { ArtifactKind } from "./domain";
import type { Provenance } from "./provider";

// §3.2/§3.4 — Capability-Port-Schemas mit GEPINNTER ArtifactKind ────────────────
//
// inputSchema/outputSchema einer Capability sind ZodObjects benannter Ports (§3.2).
// Artefakt-Ports tragen ihre `artifactKind` als `z.literal` → introspektierbar für
// den Build-Kind-Check (pipeline/zod-introspect.portKind). Signal-/Scalar-Ports
// (verdict, needsChromaKey, keyColor, score, loopScore, meta-json) tragen KEINE Kind
// und werden vom Kind-Check übersprungen (sound: nie ein Falsch-Positiv).
//
// Der Laufzeit-Wert eines Artefakt-Ports ist ein Handle auf ein job_artifacts-
// Element (D35/D42): nur `artifactKind` ist build-relevant; alle übrigen Felder
// (nodeId/port/ordinal/r2_key/checksum/…) befüllt der Runner (M5) und werden via
// `.passthrough()` beim Round-Trip durchgereicht, statt sie wegzuschneiden.

/** Ein Artefakt-Port mit gepinnter Kind (D41/D42). */
export function artifactPort<K extends ArtifactKind>(kind: K) {
  return z.object({ artifactKind: z.literal(kind) }).passthrough();
}

/** Geordnete Collection gleicher Kind — Fan-out-Quelle/-Ergebnis (D42). */
export function collectionPort<K extends ArtifactKind>(kind: K) {
  return z.array(artifactPort(kind));
}

/** Laufzeit-Handle eines aufgelösten Artefakt-Ports (vom Runner befüllt, M5). */
export type ArtifactValue<K extends ArtifactKind = ArtifactKind> = {
  artifactKind: K;
  nodeId?: string;
  port?: string;
  ordinal?: number;
  r2_key?: string | null;
  /**
   * Provider-/GPU-Output-URL (z.B. Pixverse-/fal-`videoUrl`, FLUX-`imageUrl`). Eine
   * provider-backed Capability gibt sie im Output-Handle zurück; der Runner lädt die Bytes
   * beim Persistieren via `RunnerEnv.fetchBytes` nach R2 (§3.7). `.passthrough()` der
   * Artefakt-Ports trägt sie durch die Schema-Validierung.
   */
  sourceUrl?: string;
  /**
   * Checksum/Größe eines Outputs, der bereits server-seitig nach R2 geschrieben wurde
   * (Modal↔R2-Direct, D58): die GPU-Runtime (Modal) liest/schreibt R2 selbst und gibt im
   * Output-Handle `r2_key` + `checksum` + `sizeBytes` zurück. Ein `r2_key` im Output-Handle
   * genügt dem Runner als „Bytes liegen in R2" — er verbucht den Key DIREKT in job_artifacts,
   * KEIN `fetchBytes`, kein Re-PUT (D168-Fix: auch DURCHGEREICHTE Eingangs-Artefakte ohne
   * checksum/sizeBytes, z.B. frame-smooth-No-op/D151, dürfen so passieren — Capabilities
   * erfinden nie Keys; der frühere `checksum||sizeBytes`-Zusatz-Check erzeugte Phantom-
   * Metazeilen ohne R2-Objekt). Provider-backed Outputs ohne R2-Direct nutzen weiter
   * `sourceUrl`→`fetchBytes` (D56).
   */
  checksum?: string | null;
  sizeBytes?: number | null;
  /** Provenance generierter Artefakte (D39) → Asset-Metadaten bei der Materialisierung. */
  provenance?: Provenance;
  [k: string]: unknown;
};
